import { all, call, delay, put, select, takeEvery, takeLatest } from "redux-saga/effects";
import moment from "moment";
import {
  getStates,
  handleCompanyForbiddenError,
  showNotification,
  startLoading,
  stopLoading,
} from "@shared/store/actions";
import sharedApi from "@shared/api";
import fileApi from "@containers/File/api";
import { generateId, showHttpErrorNotification } from "@shared/utils";
import { download } from "@shared/utils/request";
import {
  ActionWithPayload,
  AWSCreds,
  ORDER_COLUMN_TYPE,
  ORDER_TYPE,
  PaginatedResponse,
  ResponseError,
} from "@shared/interfaces";
import { PayPerLead, PayPerLeadDispute, PayPerLeadWithDetail } from "@shared/models/PayPerLead";
import { selectors as accountSelectors } from "@containers/Account/store";
import { Account, State } from "@shared/models";
import {
  CheckPurchaseResponse,
  CountyById,
  CountyListResponse,
  DownloadLeadsRequestPayload,
  FetchCountyOptionsPayload,
  FetchCountyPricePayload,
  FetchStateLeadPricePayload,
  GetDisputesSuccess,
  LeadFilterOptions,
  LeadPriceResponse,
  PayPerLeadPurchaseInfo,
  PayPerLeadStatistics,
  PayPerLeadSubscription,
  PayPerLeadSubscriptionWithCustomer,
  StatesById,
  SubscriptionsListQuery,
  SubscriptionsListResponse,
  SubscriptionWithCallbackPayload,
  UpdateDisputeResponse,
  SubscriptionIdsWithCallbackPayload,
  GetSubscriptionStatisticsRequest,
  GetSubscriptionStatisticsSuccess,
  ChargeQuote,
  PayPerLeadCounty,
  ApplyNewPrices,
  GetPayPerLeadSubscriptionsListShape,
  FetchFilterOptionsPayload,
} from "@containers/PayPerLead/interface";
import { checkIfAdmin } from "@shared/utils/ACL";
import { Upload } from "@aws-sdk/lib-storage";
import { S3Client } from "@aws-sdk/client-s3";
import { getUnpaidSubscriptions } from "@shared/utils/subscriptions";
import { PaymentFailedMessage } from "@shared/constants";
import {
  PayPerLeadBuyerProfile,
  PayPerLeadBuyerProfilePayload,
} from "@containers/PayPerLead/interface/PayPerLeadBuyerProfile";

import api from "../api";
import config from "../../../config";

import { actions, selectors } from "./";

function* getSubscriptionsSaga({ payload }: ActionWithPayload<SubscriptionsListQuery>) {
  try {
    yield put(startLoading());

    const subscriptions: SubscriptionsListResponse = yield call(api.getSubscriptions, payload);

    yield put(actions.getLeadSubscriptions.success(subscriptions));
  } catch (error) {
    yield put(actions.getLeadSubscriptions.failure(error as Error));
    yield put(handleCompanyForbiddenError(error as { code: number }));

    console.error(error);
  } finally {
    yield put(stopLoading());
  }
}

function* getLeadSaga({ payload }: ReturnType<typeof actions.getLead.request>) {
  try {
    yield put(startLoading());

    const lead: PayPerLeadWithDetail = yield call(api.getLead, payload);
    yield put(actions.getLead.success(lead));
  } catch (error) {
    yield put(actions.getLead.failure(error as Error));

    yield put(handleCompanyForbiddenError(error as { code: number }));
  } finally {
    yield put(stopLoading());
  }
}

function* disputeLeadSaga({ payload }: ReturnType<typeof actions.disputeLead.request>) {
  try {
    yield put(startLoading());

    let uploadObj: Upload | null = null;
    const credentials: AWSCreds = yield call(fileApi.getTempAWSCreds);
    const client = new S3Client({
      region: credentials.region,
      credentials: credentials.credentials,
    });

    const uploadTasks = [];
    const filePaths: string[] = [];

    for (const file of payload?.files || []) {
      const id = generateId();
      const extension = file.name.split(".").pop();
      const key = `dispute/${payload.lead_id}/${id}.${extension}`;

      filePaths.push(`${config.aws.s3.cdnDomainDisputes}/${key}`);

      uploadObj = new Upload({
        client,
        params: {
          Bucket: credentials.DisputesBucket,
          Key: key,
          Body: file,
          ContentDisposition: `attachment; filename=${encodeURIComponent(file.name)}`,
        },
      });

      uploadTasks.push(call([uploadObj, uploadObj.done]));
    }

    yield all(uploadTasks);

    const dispute: PayPerLeadDispute = yield call(api.disputeLead, {
      lead_id: payload.lead_id,
      message: payload.message,
      photos: filePaths,
    });

    yield put(actions.disputeLead.success(dispute));
  } catch (error) {
    yield put(actions.disputeLead.failure(error as Error));

    yield put(showHttpErrorNotification(error as ResponseError, (error as Error)?.message ?? "Could not dispute lead"));
  } finally {
    yield put(stopLoading());
  }
}

function* getLeadsSaga({ payload }: ReturnType<typeof actions.getLeads.request>) {
  try {
    yield put(startLoading());

    const leads: PaginatedResponse<PayPerLeadWithDetail> = yield call(api.getLeads, payload);
    yield put(actions.getLeads.success({ ...leads, clear: payload.page === 1 }));
  } catch (error) {
    yield put(actions.getLeads.failure(error as Error));

    yield put(handleCompanyForbiddenError(error as { code: number }));
  } finally {
    yield put(stopLoading());
  }
}

function* getLeadsCountiesSaga({ payload }: ReturnType<typeof actions.getLeadsCounties.request>) {
  try {
    yield put(startLoading());

    const leadsCounties: PaginatedResponse<PayPerLeadCounty> = yield call(api.getLeadsCounties, payload);
    yield put(actions.getLeadsCounties.success({ ...leadsCounties, clear: payload.page === 1 }));
  } catch (error) {
    yield put(actions.getLeadsCounties.failure(error as Error));

    yield put(handleCompanyForbiddenError(error as { code: number }));
  } finally {
    yield put(stopLoading());
  }
}

function* getSubscriptionsWithCustomersSaga({
  payload,
}: ReturnType<typeof actions.getSubscriptionsWithCustomers.request>) {
  try {
    yield put(startLoading());

    const customers: PaginatedResponse<PayPerLeadSubscriptionWithCustomer> = yield call(
      api.getSubscriptionsWithCustomers,
      payload,
    );
    yield put(actions.getSubscriptionsWithCustomers.success({ ...customers, clear: payload.page === 1 }));
  } catch (error) {
    yield put(actions.getSubscriptionsWithCustomers.failure(error as Error));

    yield put(handleCompanyForbiddenError(error as { code: number }));
  } finally {
    yield put(stopLoading());
  }
}

function* getCurrentMonthLeadsSaga({ payload }: ReturnType<typeof actions.getCurrentMonthLeads.request>) {
  try {
    yield put(startLoading());

    const leads: PaginatedResponse<PayPerLead> = yield call(api.getLeads, payload);
    yield put(actions.getCurrentMonthLeads.success({ ...leads, clear: payload.page === 1 }));
  } catch (error) {
    yield put(actions.getCurrentMonthLeads.failure(error as Error));

    yield put(handleCompanyForbiddenError(error as { code: number }));
  } finally {
    yield put(stopLoading());
  }
}

function* getSubscriptionDetailsSaga({
  payload: { id, draftItems },
}: ReturnType<typeof actions.getSubscriptionDetails.request>) {
  try {
    yield put(startLoading());

    const subscription: PayPerLeadSubscription = yield call(api.getSubscriptionDetails, id, draftItems);

    yield put(actions.getSubscriptionDetails.success(subscription));
  } catch (error) {
    yield put(showHttpErrorNotification(error as ResponseError, "Failed to get subscription"));
    console.error(error);
  } finally {
    yield put(stopLoading());
  }
}

function* setStatesCacheSaga({ payload }: ActionWithPayload<State[]>) {
  const map: StatesById = {};

  for (const item of payload) {
    map[item.id] = {
      value: item.id,
      label: item.name,
      abbreviation: item.abbreviation,
      counties: {},
    };
  }

  yield put(actions.cacheStatesOptions(map));
}

function* fetchCountyOptionsSaga({ payload }: ActionWithPayload<FetchCountyOptionsPayload>) {
  try {
    yield put(startLoading());

    const response: CountyListResponse = yield call(sharedApi.getCounties, payload.stateId);
    const counties: CountyById = {};

    for (const county of response.items) {
      counties[county.id] = {
        value: county.id,
        label: county.name,
      };
    }

    yield put(
      actions.fetchCountyOptions.success({
        counties,
        stateId: payload.stateId,
      }),
    );
  } catch (error) {
    yield put(showHttpErrorNotification(error as ResponseError, "Failed to load county list"));
  } finally {
    yield put(stopLoading());
  }
}

function* fetchCountyPricesSaga({ payload }: ActionWithPayload<FetchCountyPricePayload>) {
  try {
    yield put(startLoading());
    const response: LeadPriceResponse = yield call(api.getLeadPrice, payload.stateId, payload.countyId);
    yield put(
      actions.fetchCountyPrice.success({
        stateId: payload.stateId,
        countyId: payload.countyId,
        price: response.price,
      }),
    );
  } catch (error) {
    yield put(showHttpErrorNotification(error as ResponseError, "Failed to load price. Please try again later."));
  } finally {
    yield put(stopLoading());
  }
}

function* fetchStateLeadPriceSaga({ payload }: ActionWithPayload<FetchStateLeadPricePayload>) {
  try {
    yield put(startLoading());

    const response: LeadPriceResponse = yield call(api.getLeadPrice, payload.stateId);

    yield put(
      actions.fetchStateLeadPrice.success({
        stateId: payload.stateId,
        price: response.price,
      }),
    );
  } catch (error) {
    yield put(showHttpErrorNotification(error as ResponseError, "Failed to load price"));

    yield put(actions.fetchStateLeadPrice.failure(error as ResponseError));
  } finally {
    yield put(stopLoading());
  }
}

function* fetchFilterOptionsSaga({ payload }: ActionWithPayload<FetchFilterOptionsPayload>) {
  let accountId;
  const isAdmin = checkIfAdmin();

  if (!isAdmin) {
    const account: Account = yield select(accountSelectors.getActiveAccount());
    accountId = account.id;
  }

  yield put(startLoading());

  try {
    const data: LeadFilterOptions = yield call(api.getFilterOptions, {
      account_id: accountId,
      is_inactive: payload.is_inactive,
      is_full_range: payload.is_full_range,
    });
    yield put(actions.fetchFilterOptions.success(data));
  } catch (error) {
    yield put(showHttpErrorNotification(error as ResponseError));
    yield put(actions.fetchFilterOptions.failure(error as ResponseError));
  } finally {
    yield put(stopLoading());
  }
}

function* saveSubscriptionSaga({ payload }: ActionWithPayload<SubscriptionWithCallbackPayload>) {
  const account: Account = yield select(accountSelectors.getActiveAccount());

  yield put(startLoading());

  try {
    const response: PayPerLeadSubscription = payload.subscription.id
      ? yield call(api.updateSubscription, payload.subscription)
      : yield call(api.createSubscription, account.id, payload.subscription);

    yield put(actions.saveSubscription.success(response));

    payload.callback(response.id as number);
  } catch (error) {
    yield put(showHttpErrorNotification(error as ResponseError));
    yield put(actions.saveSubscription.failure(error as ResponseError));
    console.error(error);
  } finally {
    yield put(stopLoading());
  }
}

function* setSubscriptionDiscountSaga({ payload }: ReturnType<typeof actions.setSubscriptionDiscount.request>) {
  try {
    yield put(startLoading());

    const quote: ChargeQuote = yield call(api.setDiscount, payload);

    yield put(actions.setSubscriptionDiscount.success({ ...quote, ...payload }));
    yield put(
      showNotification({
        appearance: "success",
        message: `Coupon has been successfully ${payload.coupon ? "applied" : "removed"}`,
      }),
    );
  } catch (error) {
    yield put(actions.setSubscriptionDiscount.failure(error as ResponseError));
  } finally {
    yield put(stopLoading());
  }
}

function* updateSubscriptionQuoteSaga({ payload }: ActionWithPayload<PayPerLeadSubscription>) {
  if (!payload.id) {
    return;
  }

  yield put(actions.getSubscriptionQuote.request(payload.id));
}

function* getSubscriptionQuoteSaga({ payload }: ReturnType<typeof actions.getSubscriptionQuote.request>) {
  try {
    yield put(startLoading());

    const quote: ChargeQuote = yield call(api.getChargeQuote, payload);

    yield put(actions.getSubscriptionQuote.success(quote));
  } catch (error) {
    yield put(showHttpErrorNotification(error as ResponseError));
    yield put(actions.getSubscriptionQuote.failure(error as ResponseError));
  } finally {
    yield put(stopLoading());
  }
}

function* updateSubscriptionBillingSaga({
  payload: { callback, ...requestPayload },
}: ReturnType<typeof actions.updateSubscriptionBilling.request>) {
  try {
    yield put(startLoading());
    yield call(api.updateSubscriptionBilling, requestPayload);
    callback();
  } catch (error) {
    yield put(showHttpErrorNotification(error as ResponseError));
    yield put(actions.saveSubscription.failure(error as ResponseError));
  } finally {
    yield put(stopLoading());
  }
}

function* purchaseSubscriptionSaga({
  payload: { callback, onFailed, subscription, ...requestPayload },
}: ActionWithPayload<SubscriptionWithCallbackPayload>) {
  const account: Account = yield select(accountSelectors.getActiveAccount());

  yield put(startLoading());

  try {
    let id = subscription.id;
    if (!id) {
      const response: PayPerLeadSubscription = yield call(api.createSubscription, account.id, subscription);
      id = response.id as number;
    }

    const created: PayPerLeadSubscription = { ...subscription, id };

    yield call(api.purchaseSubscription, { id, ...requestPayload });
    yield put(actions.purchaseSubscription.success(created));
    callback(id);
  } catch (error) {
    yield put(showHttpErrorNotification(error as ResponseError));

    onFailed?.(account.id, error as { message: string });
  } finally {
    yield put(stopLoading());
  }
}

function* purchaseSubscriptionsSaga({ payload }: ActionWithPayload<PayPerLeadSubscription[]>) {
  yield put(startLoading());

  try {
    const subscriptionIds = payload.map((s) => s.id as number);

    // Purchase all subscriptions in parallel
    yield all(subscriptionIds.map((id) => call(api.purchaseSubscription, { id })));

    const maxAttempts = 30;
    let attempt = 0;

    while (attempt < maxAttempts) {
      attempt++;

      // Fetch updated subscriptions
      const subscriptions: SubscriptionsListResponse = yield call(api.getSubscriptions, {
        isActive: true,
        showItems: true,
      });

      yield put(actions.getLeadSubscriptions.success(subscriptions));

      // Filter for failed subscriptions
      const failedSubscriptions = getUnpaidSubscriptions(
        subscriptions.items.filter((s: PayPerLeadSubscription) => subscriptionIds.includes(s.id as number)),
      );

      // If no failed subscriptions, exit the loop
      if (!failedSubscriptions.length) break;

      // Wait before retrying
      yield delay(1000);
    }

    // Handle case where max attempts are reached
    if (attempt >= maxAttempts) {
      const error: ResponseError = {
        code: 400,
        message: PaymentFailedMessage,
        customMessage: true,
      };
      yield put(showHttpErrorNotification(error));
    }
  } catch (error) {
    // Handle errors
    yield put(showHttpErrorNotification(error as ResponseError));
  } finally {
    // Stop loading state
    yield put(stopLoading());
  }
}

function* awaitPurchaseProcessedSaga({
  payload: { callback, onFailed, subscription, ...requestPayload },
}: ActionWithPayload<SubscriptionWithCallbackPayload>) {
  const maxAttempts = 30;
  let attempt = 0;
  if (subscription.id && subscription.quote) {
    try {
      yield put(startLoading());

      do {
        attempt++;
        const response: CheckPurchaseResponse = yield call(api.checkPurchase, subscription.id);

        if (response.paid) {
          callback(subscription.id as number);
          yield put(
            showNotification({
              appearance: "success",
              message: "Your Pay Per Lead subscription has been successfully activated",
            }),
          );
          break;
        }

        if (!response.hasPendingInvoice) {
          yield call(api.purchaseSubscription, { id: subscription.id, ...requestPayload });
        }

        if (attempt > maxAttempts) {
          const error: ResponseError = {
            code: 400,
            message: PaymentFailedMessage,
            customMessage: true,
          };

          yield put(showHttpErrorNotification(error));
          yield put(actions.awaitPurchaseProcessed.failure(error));
          if (onFailed) {
            onFailed(subscription.id);
          }
          break;
        }
        yield delay(1000);
      } while (true);
    } catch (error) {
      yield put(showHttpErrorNotification(error as ResponseError));
      yield put(actions.awaitPurchaseProcessed.failure(error as ResponseError));

      if (onFailed) {
        onFailed(subscription.id);
      }

      console.error(error);
    } finally {
      yield put(stopLoading());
    }
  }
}

function* fetchTotalPaymentAmountSaga({ payload }: ActionWithPayload<SubscriptionIdsWithCallbackPayload>) {
  if (payload.ids?.length) {
    try {
      yield put(startLoading());
      const responses: PayPerLeadPurchaseInfo[] = yield all(payload.ids.map((id) => call(api.checkPurchase, id)));
      const paymentAmount = responses.reduce((acc, purchaseInfo) => acc + purchaseInfo.paymentAmount, 0);
      payload.callback(paymentAmount);
    } catch (error) {
      yield showHttpErrorNotification(error as ResponseError);
    } finally {
      yield put(stopLoading());
    }
  }
}

function* fetchPurchaseInfoSaga({ payload: id }: ActionWithPayload<number>) {
  if (id) {
    try {
      yield put(startLoading());
      const response: PayPerLeadPurchaseInfo = yield call(api.checkPurchase, id);
      yield put(actions.fetchPurchaseInfo.success(response));
    } catch (error) {
      yield put(showHttpErrorNotification(error as ResponseError));
    } finally {
      yield put(stopLoading());
    }
  }
}

function* getAdminDisputesSaga({ payload }: ReturnType<typeof actions.getDisputes.request>) {
  try {
    yield put(startLoading());
    const disputes: GetDisputesSuccess = yield call(api.getDisputes, payload);
    yield put(actions.getDisputes.success(disputes));
  } catch (error) {
    yield put(actions.getDisputes.failure(error as Error));
  } finally {
    yield put(stopLoading());
  }
}

function* updateAdminDisputesSaga({ payload }: ReturnType<typeof actions.updateDispute.request>) {
  try {
    yield put(startLoading());
    const response: UpdateDisputeResponse = yield call(api.updateDispute, payload);
    yield put(actions.updateDispute.success({ ...payload, ...response }));
  } catch (error) {
    yield put(actions.updateDispute.failure(error as Error));
  } finally {
    yield put(stopLoading());
  }
}

function* getStatisticsSaga({ payload }: ReturnType<typeof actions.getStatistics.request>) {
  try {
    yield put(startLoading());
    const statistics: PayPerLeadStatistics = yield call(api.getStatistics, payload);
    yield put(actions.getStatistics.success(statistics));
  } catch (error) {
    yield put(actions.getStatistics.failure(error as Error));
  } finally {
    yield put(stopLoading());
  }
}

function* getSubscriptionsListSaga({ payload }: ActionWithPayload<GetPayPerLeadSubscriptionsListShape>) {
  try {
    yield put(startLoading());

    const subscriptions: PaginatedResponse<PayPerLeadSubscription> = yield call(api.getSubscriptionsList, payload);
    yield put(actions.getSubscriptionsList.success(subscriptions));
  } catch (error) {
    yield put(actions.getSubscriptionsList.failure(error as ResponseError));
  } finally {
    yield put(stopLoading());
  }
}

function* deactivateSubscriptionSaga({ payload }: ReturnType<typeof actions.deactivateSubscription.request>) {
  if (!payload.subscription.id) {
    return;
  }

  try {
    const cancellationReason = payload.cancellation_reason ?? {};
    yield put(startLoading());
    yield call(api.deactivateSubscription, payload.subscription.id, cancellationReason);
    yield put(actions.deactivateSubscription.success(payload.subscription));
    payload.callback(payload.subscription.id);
  } catch (error) {
    yield put(actions.deactivateSubscription.failure(error as ResponseError));
    yield put(showHttpErrorNotification(error as ResponseError));
  } finally {
    yield put(stopLoading());
  }
}

function* qualifyLeadsSaga({ payload }: ReturnType<typeof actions.qualifyLeads.request>) {
  try {
    const account: Account = yield select(accountSelectors.getActiveAccount());

    yield call(api.qualifyLeads, {
      ids: payload.ids,
      is_qualified: payload.is_qualified,
      account_id: account.id,
    });

    if (payload.notify && payload.is_qualified !== null) {
      const subject = payload.ids.length > 1 ? "properties" : "property";
      const status = payload.is_qualified ? "Quality Seller Lead" : "Not Quality";

      yield put(
        showNotification({
          appearance: "success",
          message: `${payload.ids.length} ${subject} marked as ${status}`,
        }),
      );
    }

    yield put(actions.qualifyLeads.success(payload));
  } catch (error) {
    yield put(actions.qualifyLeads.failure(error as ResponseError));
    yield put(showHttpErrorNotification(error as ResponseError));

    if (payload.onError) {
      payload.onError(error as Error);
    }
  }
}

function* downloadLeadsCSVSaga({ payload }: ReturnType<typeof actions.downloadLeadsCSV.request>) {
  try {
    yield put(startLoading());
    const params: DownloadLeadsRequestPayload = {
      ids: payload.ids,
      order: ORDER_COLUMN_TYPE.CREATED_AT,
      direction: ORDER_TYPE.DESC,
    };

    if (payload.active_account) {
      const account: Account = yield select(accountSelectors.getActiveAccount());
      params.account_id = account.id;
    }

    const response: Blob = yield call(api.exportLeads, params);

    const now = moment().format("ll");

    yield call(download, URL.createObjectURL(response), `Leads ${now}.csv`);

    yield put(
      showNotification({
        message: "Your file was successfully downloaded",
        appearance: "success",
      }),
    );
  } catch (error) {
    yield put(actions.qualifyLeads.failure(error as ResponseError));
    yield put(showHttpErrorNotification(error as ResponseError));
  } finally {
    yield put(stopLoading());
  }
}

function* fetchSubscriptionStatisticsSaga({ payload }: ActionWithPayload<GetSubscriptionStatisticsRequest>) {
  try {
    yield put(startLoading());

    const response: GetSubscriptionStatisticsSuccess = yield call(api.getSubscriptionStatistics, payload);

    yield put(actions.getSubscriptionStatistics.success(response));
  } catch (error) {
    yield put(actions.fetchStateLeadPrice.failure(error as ResponseError));
  } finally {
    yield put(stopLoading());
  }
}

function* pauseSubscription({ payload }: ActionWithPayload<{ id: number; is_paused: boolean }>) {
  try {
    yield put(startLoading());

    yield call(api.pauseSubscription, payload);

    yield put(actions.pauseSubscription.success(payload));

    yield put(
      showNotification({
        message: `Subscription has been successfully ${payload.is_paused ? "paused" : "activated"}`,
        appearance: "success",
      }),
    );
  } catch (error) {
    yield put(showHttpErrorNotification(error as ResponseError));
  } finally {
    yield put(stopLoading());
  }
}

function* applyNewPrices({ payload: { callback, ...data } }: ActionWithPayload<ApplyNewPrices>) {
  try {
    yield put(startLoading());

    yield call(api.applyNewPrices, data);

    callback?.();
  } catch (error) {
    yield put(actions.applyNewPrices.failure(error as ResponseError));
  } finally {
    yield put(stopLoading());
  }
}

function* fetchBuyerProfileSaga({ payload }: ReturnType<typeof actions.fetchBuyerProfile.request>) {
  try {
    yield put(startLoading());
    const buyerProfile: PayPerLeadBuyerProfile = yield call(api.getBuyerProfile, payload);
    yield put(actions.fetchBuyerProfile.success(buyerProfile));
  } catch (error) {
    yield put(actions.fetchBuyerProfile.failure(error as ResponseError));
  } finally {
    yield put(stopLoading());
  }
}

function* processBuyerProfileSaga({
  payload: { files, buyerProfile: buyerProfilePayload, account_id },
}: ActionWithPayload<PayPerLeadBuyerProfilePayload>) {
  try {
    yield put(startLoading());

    const uploadTasks = [];
    let credentials: AWSCreds | null = null;
    let client: S3Client | null = null;

    // Loop through files to upload
    for (const [key, file] of Object.entries(files || {})) {
      if (!file) continue; // Skip null values

      // Fetch credentials and client only if needed
      if (!credentials) {
        credentials = yield call(fileApi.getTempAWSCreds);
        client = new S3Client({
          region: credentials?.region,
          credentials: credentials?.credentials,
        });
      }

      // Ensure credentials and client are not null before proceeding
      if (credentials && client) {
        const generatedId = generateId();
        const extension = file.name.split(".").pop();
        const s3Key = `ppl/buyers/${account_id}/${generatedId}.${extension}`;
        (buyerProfilePayload as Record<string, string>)[key] = `${config.aws.s3.cdnDomainBuyers}/${s3Key}`;

        const uploadObj = new Upload({
          client,
          params: {
            Bucket: credentials.pplBucket,
            Key: s3Key,
            Body: file,
            ContentDisposition: `attachment; filename=${encodeURIComponent(file.name)}`,
          },
        });

        uploadTasks.push(call([uploadObj, uploadObj.done]));
      }
    }

    // Only execute upload tasks if there are files
    if (uploadTasks.length > 0) {
      yield all(uploadTasks);
    }

    const buyerProfile: PayPerLeadBuyerProfile = yield select(selectors.getPPLBuyerProfile());
    if (buyerProfile) {
      yield call(api.updateBuyerProfile, { account_id, buyerProfile: buyerProfilePayload });
    } else {
      yield call(api.createBuyerProfile, { account_id, buyerProfile: buyerProfilePayload });
    }
    yield put(
      showNotification({
        message: "Your buyer profile has been successfully saved.",
        appearance: "success",
      }),
    );
  } catch (error) {
    yield put(actions.processBuyerProfile.failure(error as ResponseError));
    yield put(
      showHttpErrorNotification(error as ResponseError, (error as Error)?.message ?? "Could not save buyer profile"),
    );
  } finally {
    yield put(actions.fetchBuyerProfile.request(account_id));
    yield put(stopLoading());
  }
}

function* payPerLeadsSagas() {
  yield takeLatest(getStates.success, setStatesCacheSaga);
  yield takeEvery(actions.fetchCountyOptions.request, fetchCountyOptionsSaga);
  yield takeEvery(actions.fetchStateLeadPrice.request, fetchStateLeadPriceSaga);
  yield takeEvery(actions.fetchCountyPrice.request, fetchCountyPricesSaga);
  yield takeLatest(actions.fetchFilterOptions.request, fetchFilterOptionsSaga);
  yield takeLatest(actions.getLeadSubscriptions.request, getSubscriptionsSaga);
  yield takeLatest(actions.purchaseSubscription.request, purchaseSubscriptionSaga);
  yield takeLatest(actions.purchaseSubscriptions.request, purchaseSubscriptionsSaga);
  yield takeLatest(actions.awaitPurchaseProcessed.request, awaitPurchaseProcessedSaga);
  yield takeLatest(actions.getSubscriptionDetails.request, getSubscriptionDetailsSaga);
  yield takeLatest(actions.saveSubscription.request, saveSubscriptionSaga);
  yield takeLatest(actions.setSubscriptionDiscount.request, setSubscriptionDiscountSaga);
  yield takeLatest(actions.getSubscriptionQuote.request, getSubscriptionQuoteSaga);
  yield takeLatest(actions.saveSubscription.success, updateSubscriptionQuoteSaga);
  yield takeLatest(actions.getSubscriptionDetails.success, updateSubscriptionQuoteSaga);
  yield takeLatest(actions.updateSubscriptionBilling.request, updateSubscriptionBillingSaga);
  yield takeLatest(actions.deactivateSubscription.request, deactivateSubscriptionSaga);
  yield takeLatest(actions.getLead.request, getLeadSaga);
  yield takeLatest(actions.getLeads.request, getLeadsSaga);
  yield takeLatest(actions.getLeadsCounties.request, getLeadsCountiesSaga);
  yield takeLatest(actions.disputeLead.request, disputeLeadSaga);
  yield takeLatest(actions.getCurrentMonthLeads.request, getCurrentMonthLeadsSaga);
  yield takeLatest(actions.getSubscriptionsWithCustomers.request, getSubscriptionsWithCustomersSaga);
  yield takeLatest(actions.getDisputes.request, getAdminDisputesSaga);
  yield takeLatest(actions.updateDispute.request, updateAdminDisputesSaga);
  yield takeLatest(actions.getStatistics.request, getStatisticsSaga);
  yield takeLatest(actions.qualifyLeads.request, qualifyLeadsSaga);
  yield takeLatest(actions.downloadLeadsCSV.request, downloadLeadsCSVSaga);
  yield takeLatest(actions.fetchPurchaseInfo.request, fetchPurchaseInfoSaga);
  yield takeLatest(actions.fetchTotalPaymentAmount.request, fetchTotalPaymentAmountSaga);
  yield takeLatest(actions.getSubscriptionStatistics.request, fetchSubscriptionStatisticsSaga);
  yield takeLatest(actions.applyNewPrices.request, applyNewPrices);
  yield takeLatest(actions.fetchBuyerProfile.request, fetchBuyerProfileSaga);
  yield takeLatest(actions.processBuyerProfile.request, processBuyerProfileSaga);
  yield takeLatest(actions.getSubscriptionsList.request, getSubscriptionsListSaga);
  yield takeLatest(actions.pauseSubscription.request, pauseSubscription);
}

export default payPerLeadsSagas;
